import React from "react";
import styled, { css } from "styled-components";

import ProcessingSpinner from "core/ProcessingSpinner";

import breakpoints from "core/breakpoints";
import { easingSeconds } from "core/animation";
import { bold, regular, medium } from "core/fontStyles";
import Flexbox from "core/Flexbox";
import colors from "core/colors";
import { CustomFocusStyle } from "core/Typography";

const ButtonBody = ({
    disabled,
    children,
    className,
    onClick,
    href,
    target,
    processing,
    size
}) => {
    const buttonText = processing ? (
        <Flexbox inlineFlex alignItems="center">
            {children} {processing && <ProcessingSpinner />}
        </Flexbox>
    ) : (
        children
    );

    // if a href is passed, it will return an anchor. otherwise, it will return a button
    return href ? (
        <a
            className={className}
            disabled={disabled}
            href={href}
            target={target}
        >
            {buttonText}
        </a>
    ) : (
        <button className={className} disabled={disabled} onClick={onClick}>
            {buttonText}
        </button>
    );
};

const sizes = {
    small: css`
        font-size: 0.75rem;
        line-height: 0.75rem;
        padding-top: 0rem;
        padding-bottom: 0rem;
    `,
    medium: css`
        font-size: 0.875rem;
        line-height: 0.9375rem;
        padding-top: 0.8125rem;
        padding-bottom: 0.8125rem;
    `,
    large: css`
        font-size: 1rem;
        line-height: 1rem;
        padding-top: 1rem;
        padding-bottom: 1rem;
    `
};

export const ButtonStyle = css`
    ${medium}
    width: ${props => props.width || "100%"};
    color: ${colors.snow};
    border: 0;
    border-radius: 3px;
    ${({ removeBold }) => !removeBold && bold};
    ${props => sizes[props.size]};
    transition: all ${easingSeconds};
    text-align: center;
    text-decoration: none;

    &:hover {
        cursor: pointer;
    }

    &:disabled {
        background-color: ${colors.coin};
        pointer-events: none;
    }

    &:focus {
        outline: solid 3px rgba(62, 169, 245, 0.4);
        outline-offset: 0.25rem;
    }
`;

const buttonProcessingStyles = css`
    &,
    &:hover,
    &:disabled {
        background-color: ${colors.marketingCtaPressed};
        color: ${colors.midnight};
        transition: all ${easingSeconds};
    }

    ${ProcessingSpinner} {
        margin-left: 0.5rem;
    }
`;

export const Button = styled(ButtonBody)`
    ${ButtonStyle};
    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const BlackButtonStyle = css`
    background-color: #000000;

    &:hover {
        background-color: #404040;
    }

    &:active {
        background-color: ${colors.midnight};
    }

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const BlackButton = styled(Button)`
    ${BlackButtonStyle};
`;

export const GrayButtonStyle = css`
    background-color: #404040;

    &:hover {
        background-color: #1f1f1f;
    }

    &:active {
        background-color: #000000;
    }

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const GrayButton = styled(Button)`
    ${GrayButtonStyle};
`;

export const BlueButtonStyle = css`
    background-color: #3ea9f5;

    &:hover {
        background-color: #2e80b7;
    }

    &:active {
        background-color: #1f587f;
    }

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const BlueButton = styled(Button)`
    ${BlueButtonStyle};
`;

// new BlackButton style - see Leadership section on About page for example:
export const NewBlackButton = styled(BlackButton)`
    letter-spacing: 0;
    display: inline-block;
    padding: 1rem; /* 16px */

    /* Mobile: */
    @media (max-width: ${breakpoints.beforeTablet}rem) {
        width: 100%;
    }

    /* Tablet & Desktop: */
    min-width: 12.5rem; /* 200px */
    width: auto;

    &:focus {
        outline: 0;
        ${colors.focusBorder}
    }

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const RoyalBlueButtonStyle = css`
    ${regular}
    letter-spacing: 0;
    font-size: 0.9375rem; /* 15px */
    line-height: 1rem; /* 16px */
    padding: 1rem; /* 16px */
    color: ${colors.mist};
    background: ${colors.ultramarine};

    &:hover {
        color: ${colors.mist};
        background: ${colors.mutedRoyalBlue};
    }

    ${CustomFocusStyle}

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const RoyalBlueButton = styled(Button)`
    ${RoyalBlueButtonStyle}
`;

export const LightBlueButtonStyle = css`
    ${regular}
    letter-spacing: 0;
    font-size: 0.9375rem; /* 15px */
    line-height: 1rem; /* 16px */
    padding: 1rem; /* 16px */
    color: ${colors.midnight};
    background: ${colors.maya};

    &:hover {
        color: ${colors.mist};
        background: ${colors.mutedSoftBlue};
    }

    ${CustomFocusStyle}

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const LightBlueButton = styled(Button)`
    ${LightBlueButtonStyle}
`;

export const WhiteOutlineButtonStyle = css`
    /* This button will probably end up being the "small" version when we have time to implement new reusable buttons: */
    ${regular}
    font-size: 0.8125rem; /* 13px */
    line-height: 0.8125rem; /* 13px */
    padding: 0.75rem; /* 12px */
    background: ${colors.snow};
    color: ${colors.midnight};
    border: 1px solid ${colors.concrete};

    &:hover {
        color: ${colors.midnight};
        background: ${colors.lightGrey};
        border-color: ${colors.coin};
    }

    ${CustomFocusStyle}

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const WhiteOutlineButton = styled(Button)`
    ${WhiteOutlineButtonStyle};
`;

const PillOutline = `
    inset 0 0 0 2px ${colors.concrete}
`;

const PillHoverOutline = `
    inset 0 0 0 2px ${colors.coin}
`;

const PillFocusOutline = `
    ${PillOutline}, 0 0 0 3px ${colors.focus}
`;

export const PillOutlineButtonStyle = css`
    padding: 1rem 1.5rem; /* 16px 24px */
    border-radius: 3rem; /* 48px */
    box-shadow: ${PillOutline};
    /* ^^ use inset outline because using border will affect height of button */
    background: transparent;
    text-decoration: none;
    ${regular};
    color: ${colors.midnight};
    font-size: 0.9375rem; /* 15px */
    line-height: 1rem; /* 16px */
    transition: all ${easingSeconds};
    display: inline-block;
    width: auto;

    &:not(:last-of-type) {
        margin-right: 1.5rem; /* 24px */
    }

    &:hover {
        background-color: ${colors.lightGrey};
        box-shadow: ${PillHoverOutline};
    }

    &:active {
        outline: 0;
        background-color: ${colors.marketingCtaPressed};
        border-color: transparent;
    }

    &:focus {
        outline: 0;
        box-shadow: ${PillFocusOutline};
        /* ^^ use both outlines because using border will affect height of button & we need the inset border and focus ring */
    }
`;

export const PillOutlineButton = styled(Button)`
    ${PillOutlineButtonStyle}
`;

export const NewWhiteButtonStyle = css`
    color: ${colors.midnight};
    background-color: ${colors.mist};

    ${({ processing }) => processing && buttonProcessingStyles};
`;

export const NewWhiteButton = styled(Button)`
    ${NewWhiteButtonStyle}
`;
